/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   อัตราแลกเปลี่ยนรายเดือน — ฝ่ายบัญชีเป็นผู้คีย์

   1 งวด (เดือน/ปี) ต่อ 1 สกุลเงิน เก็บได้ค่าเดียว
   คีย์ซ้ำงวดเดิมจะเป็นการอัปเดตค่าให้เป็นค่าล่าสุด
   รายงาน/Invoice จะดึงเรตของงวดนั้นไปคูณเป็นเงินบาท
============================================================ */
import { useEffect, useState } from "react";
import {
  Card, Table, Form, Row, Col, Select, InputNumber, Input, Button,
  Space, Flex, Typography, Popconfirm, DatePicker, message,
} from "antd";
import { SaveFilled, SearchOutlined, EditOutlined } from "@ant-design/icons";
import { RiDeleteBin5Line } from "react-icons/ri";
import dayjs from "dayjs";

import FxRateService from "../../../service/ERP/FxRate.service";

const fxService = FxRateService();

const CURRENCIES = ["USD", "EUR", "GBP", "JPY", "CNY", "AUD", "SGD", "MYR"];

const money = (v) =>
  Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 6,
  });

export default function FxRateAccess() {
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const loadData = (values = null) => {
    const v = values ?? searchForm.getFieldsValue();
    const parm = {
      year: v?.year ? dayjs(v.year).format("YYYY") : "",
      currency: v?.currency || "",
    };
    fxService
      .search(parm, { ignoreLoading: true })
      .then((res) => setRows(res.data?.data || []))
      .catch(() => message.error("โหลดข้อมูลไม่สำเร็จ"));
  };

  useEffect(() => {
    form.setFieldsValue({ period: dayjs(), currency: "USD" });
    loadData({ year: dayjs() });
  }, []);

  const handleSave = () => {
    form.validateFields().then((v) => {
      const payload = {
        id: editing?.id,
        period: dayjs(v.period).format("YYYY-MM"),
        currency: v.currency,
        rate: v.rate,
        remark: v.remark || null,
      };
      const action = editing ? fxService.update(payload) : fxService.create(payload);
      action
        .then((res) => {
          message.success(res.data?.message || "บันทึกเรียบร้อย");
          setEditing(null);
          form.setFieldsValue({ rate: null, remark: null });
          loadData();
        })
        .catch((err) =>
          message.error(err?.response?.data?.message || "บันทึกไม่สำเร็จ")
        );
    });
  };

  const handleEdit = (rec) => {
    setEditing(rec);
    form.setFieldsValue({
      period: dayjs(rec.period),
      currency: rec.currency,
      rate: Number(rec.rate),
      remark: rec.remark,
    });
  };

  const handleDelete = (rec) => {
    fxService
      .deleted(rec.id)
      .then(() => {
        message.success("ลบเรียบร้อย");
        loadData();
      })
      .catch((err) => message.error(err?.response?.data?.message || "ลบไม่สำเร็จ"));
  };

  const columns = [
    {
      title: "งวด",
      dataIndex: "period",
      key: "period",
      width: 130,
      align: "center",
      render: (v) => dayjs(v).format("MM/YYYY"),
    },
    { title: "สกุลเงิน", dataIndex: "currency", key: "currency", width: 120, align: "center" },
    {
      title: "อัตรา (บาท ต่อ 1 หน่วย)",
      dataIndex: "rate",
      key: "rate",
      width: 200,
      align: "right",
      render: money,
    },
    { title: "หมายเหตุ", dataIndex: "remark", key: "remark", ellipsis: true },
    { title: "บันทึกโดย", dataIndex: "created_name", key: "created_name", width: 180 },
    {
      title: "",
      key: "action",
      width: 100,
      align: "center",
      render: (_, rec) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(rec)} />
          <Popconfirm
            title="ลบเรตนี้?"
            okText="ลบ"
            cancelText="ยกเลิก"
            onConfirm={() => handleDelete(rec)}
          >
            <Button size="small" danger icon={<RiDeleteBin5Line />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ display: "flex" }}>
      <Card>
        <Typography.Title level={3} className="m-0 !text-zinc-800">
          อัตราแลกเปลี่ยนรายเดือน
        </Typography.Title>
        <Typography.Text type="secondary">
          ฝ่ายบัญชีคีย์เรตของแต่ละเดือน — 1 งวดต่อ 1 สกุลเงิน คีย์ซ้ำงวดเดิมคือการแก้ค่าให้เป็นค่าล่าสุด
        </Typography.Text>
      </Card>

      <Card title={editing ? `แก้ไขเรต ${editing.currency}` : "เพิ่มเรตใหม่"}>
        <Form form={form} layout="vertical" autoComplete="off">
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} md={5}>
              <Form.Item
                label="งวด (เดือน/ปี)"
                name="period"
                rules={[{ required: true, message: "กรุณาเลือกงวด" }]}
              >
                <DatePicker picker="month" format="MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Form.Item
                label="สกุลเงิน"
                name="currency"
                rules={[{ required: true, message: "กรุณาระบุสกุลเงิน" }]}
              >
                <Select
                  showSearch
                  placeholder="เช่น USD"
                  options={CURRENCIES.map((c) => ({ value: c, label: c }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Form.Item
                label="อัตรา (บาท ต่อ 1 หน่วย)"
                name="rate"
                rules={[{ required: true, message: "กรุณากรอกอัตรา" }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  controls={false}
                  style={{ width: "100%" }}
                  placeholder="เช่น 31.50"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="หมายเหตุ" name="remark">
                <Input placeholder="เช่น เรตบัญชีประจำเดือน" />
              </Form.Item>
            </Col>
            <Col xs={24} md={3}>
              <Form.Item label=" ">
                <Space>
                  <Button type="primary" icon={<SaveFilled />} onClick={handleSave}>
                    บันทึก
                  </Button>
                  {editing && (
                    <Button
                      onClick={() => {
                        setEditing(null);
                        form.setFieldsValue({ rate: null, remark: null });
                      }}
                    >
                      ยกเลิก
                    </Button>
                  )}
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card>
        <Form form={searchForm} layout="vertical" autoComplete="off">
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} md={5}>
              <Form.Item label="ปี" name="year" initialValue={dayjs()}>
                <DatePicker picker="year" format="YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Form.Item label="สกุลเงิน" name="currency">
                <Select
                  allowClear
                  placeholder="ทั้งหมด"
                  options={CURRENCIES.map((c) => ({ value: c, label: c }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label=" ">
                <Flex gap={8}>
                  <Button icon={<SearchOutlined />} onClick={() => loadData()}>
                    ค้นหา
                  </Button>
                </Flex>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Table
          size="small"
          bordered
          rowKey="id"
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 12, showSizeChanger: false }}
          locale={{ emptyText: <span>ยังไม่มีเรตของปีนี้</span> }}
        />
      </Card>
    </Space>
  );
}
